import { useSeo } from "../lib/seo";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { loginHref, parseRole } from "../data/auth";
import { clearSession, resetPassword } from "../lib/auth";
import { toast } from "../lib/toast";

import AuthLayout, { AuthHeading, authLink } from "../components/auth/AuthLayout";
import { FormAlert, PasswordField } from "../components/ui/FormField";
import { newPasswordRules } from "../components/auth/rules";
import { useT } from "../i18n";
import Button from "../components/ui/Button";

interface ResetValues {
  password: string;
  confirmPassword: string;
}

/** Opened from the e-mailed link: /reset-password?role=company&token=… */
export default function ResetPassword() {
  const t = useT();
  useSeo({ title: t.seo.resetPassword, noindex: true }); // account pages stay out of search results
  const r = t.auth.reset;
  const [searchParams] = useSearchParams();
  const role = parseRole(searchParams.get("role"));
  const token = searchParams.get("token") ?? "";
  const forgotHref = role === "driver" ? "/forgot-password" : `/forgot-password?role=${role}`;

  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({ defaultValues: { password: "", confirmPassword: "" } });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      toast.success(await resetPassword({ role, token, ...values }));
      // The backend signs out every session of the account, this one included.
      clearSession();
      setDone(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : t.common.somethingWrong;
      setSubmitError(message);
      toast.error(message);
    }
  });

  return (
    <AuthLayout>
      <Link
        to={loginHref(role)}
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-muted no-underline hover:text-ink"
      >
        <ArrowLeft size={14} />
        {t.common.login}
      </Link>

      <AuthHeading title={r.title} subtitle={r.subtitle} />

      {!token ? (
        <FormAlert title={r.missingTitle}>
          {r.missingText}{" "}
          <Link to={forgotHref} className={authLink}>
            {r.requestNew}
          </Link>
        </FormAlert>
      ) : done ? (
        <div className="grid gap-4">
          <FormAlert tone="success" title={r.doneTitle}>
            {r.doneText}
          </FormAlert>
          <Link
            to={loginHref(role)}
            className="flex h-11 items-center justify-center rounded bg-brand text-sm font-medium text-white no-underline hover:bg-brand-dark"
          >
            {t.common.login}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} noValidate className="grid gap-3.5">
          <PasswordField
            label={r.newPassword}
            placeholder={t.common.ph.newPassword}
            autoComplete="new-password"
            error={errors.password?.message}
            {...register("password", newPasswordRules(t))}
          />
          <PasswordField
            label={t.auth.driverSignup.confirmPassword}
            placeholder={t.common.ph.confirmPassword}
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            {...register("confirmPassword", {
              required: t.auth.driverSignup.confirmRequired,
              validate: (value, values) => value === values.password || t.auth.driverSignup.mismatch,
            })}
          />

          {submitError && (
            <FormAlert>
              {submitError}{" "}
              <Link to={forgotHref} className={authLink}>
                {r.requestNew}
              </Link>
            </FormAlert>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="mt-1.5 h-11 w-full cursor-pointer border-0"
          >
            {isSubmitting ? r.submitting : r.submit}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
